import type { Aircraft, AirportConfigSet, AirportConfigUpdate, FlightOccurrenceEvent, FlightOccurrenceEventKind, FlightOccurrenceStatus, FlightPlan, SaveGame, ScheduleSource, ServiceDay, WorldData } from "../../../domain/types";
import { SERVICE_DAY_LABELS } from "../../../domain/rules/schedule";
import { defaultAircraftCapabilities } from "../../../domain/rules/aircraft";
import { formatProgramStatus } from "../../format";
import { SERVICE_DAY_ORDER } from "./gmTypes";


export type AircraftDraft = Omit<Aircraft, "id"> & { id?: string };

export function MiniStat({ label, value, tone = "neutral" }: { label: string; value: number | string; tone?: "neutral" | "good" | "warn" | "bad" }) {
  return (
    <div className={`gm-mini-stat ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function ServiceDayToggleRow({ value, onChange }: { value: ServiceDay[]; onChange: (value: ServiceDay[]) => void }) {
  const toggle = (day: ServiceDay) => {
    const next = toggledServiceDays(value, day);
    onChange(next);
  };
  return (
    <div className="service-day-toggle" aria-label="服务日">
      {Object.entries(SERVICE_DAY_LABELS).map(([day, label]) => (
        <button key={day} type="button" className={serviceDayButtonClassName(value, day as ServiceDay)} onClick={() => toggle(day as ServiceDay)}>
          {label}
        </button>
      ))}
    </div>
  );
}

export function formatFlightPlanLabel(plan: FlightPlan | undefined): string {
  if (!plan) {
    return "";
  }
  return `${plan.flightNo} ${formatServiceDays(plan.serviceDays)} ${plan.origin}-${plan.destination} / ${formatProgramStatus(plan.active)}`;
}

export function formatOccurrenceLabel(occurrence: ScheduleSource["occurrences"][number] | undefined): string {
  if (!occurrence) {
    return "";
  }
  return `${occurrence.flightNo} D${occurrence.day} ${occurrence.dateIso} ${occurrence.origin}-${occurrence.destination} / ${formatOccurrenceStatus(occurrence.status)}`;
}

export function formatServiceDays(serviceDays: ServiceDay[]): string {
  return SERVICE_DAY_ORDER.filter((day) => serviceDays.includes(day)).map((day) => SERVICE_DAY_LABELS[day]).join("/") || "--";
}

export function formatOccurrenceStatus(status: FlightOccurrenceStatus): string {
  if (status === "scheduled") return "计划";
  if (status === "cancelled") return "取消";
  return "完成";
}

export function formatOccurrenceKind(kind: FlightOccurrenceEventKind): string {
  if (kind === "weather") return "天气";
  if (kind === "mechanical") return "机务";
  if (kind === "medical_transfer") return "医疗转运";
  if (kind === "security") return "安保";
  if (kind === "airport_visibility") return "机场能见度";
  return "运行";
}

export function formatOccurrenceEventSummary(events: FlightOccurrenceEvent[], cancellationReason?: string): string {
  const active = events.filter((event) => event.status === "active");
  if (active.length > 0) {
    return `${active.length} 活动 / ${active.map((event) => formatOccurrenceTrigger(event.trigger)).join(",")}`;
  }
  if (cancellationReason) {
    return `取消：${cancellationReason}`;
  }
  return historicalOccurrenceEventSummary(events);
}

function toggledServiceDays(value: ServiceDay[], day: ServiceDay): ServiceDay[] {
  if (value.includes(day)) {
    return value.filter((item) => item !== day);
  }
  return [...value, day];
}

function serviceDayButtonClassName(value: ServiceDay[], day: ServiceDay): string {
  if (value.includes(day)) {
    return "active";
  }
  return "";
}

function historicalOccurrenceEventSummary(events: FlightOccurrenceEvent[]): string {
  if (events.length > 0) {
    return `${events.length} 历史`;
  }
  return "--";
}

export function formatOccurrencePhase(phase: FlightOccurrenceEvent["phase"]): string {
  if (phase === "pre_departure") return "起飞前";
  if (phase === "enroute") return "空中";
  if (phase === "arrival") return "进近/到达";
  return "地面";
}

export function formatOccurrenceTrigger(trigger: FlightOccurrenceEvent["trigger"]): string {
  if (!trigger) {
    return "按节点";
  }
  const reference = formatOccurrenceTriggerReference(trigger.reference);
  return `${reference}+${trigger.offsetMinutes}m`;
}

export function formatOccurrenceTriggerReference(reference: NonNullable<FlightOccurrenceEvent["trigger"]>["reference"]): string {
  if (reference === "takeoff") {
    return "离地";
  }
  return "开保障";
}

export function matchesQuery(values: Array<string | number | undefined>, query: string): boolean {
  const normalized = normalizeQuery(query);
  if (!normalized) {
    return true;
  }
  return values.some((value) => normalizeQuery(String(value ?? "")).includes(normalized));
}

function normalizeQuery(value: string): string {
  return value.trim().toUpperCase();
}

export function blankAircraftDraft(save: SaveGame | null, world: WorldData): AircraftDraft {
  const typeId = world.aircraftTypes[0]?.id ?? "a320";
  const type = world.aircraftTypes.find((item) => item.id === typeId);
  const defaults = defaultAircraftCapabilities(type);
  const location = save?.fleet[0]?.location ?? world.airports[0]?.code ?? "PVG";
  return {
    registration: "",
    typeId,
    status: "available",
    location,
    etopsMinutes: defaults.etopsMinutes,
    maxTakeoffWeightKg: defaults.maxTakeoffWeightKg,
    ilsCategory: defaults.ilsCategory,
    fuelOnBoardKg: Math.round((type?.maxFuelKg ?? 18_000) * 0.5),
    hoursSinceMaintenance: 0,
    cyclesSinceCheck: 0,
    availableFromDay: save?.day ?? 1
  };
}

export function aircraftDraftFromAircraft(aircraft: Aircraft): AircraftDraft {
  return {
    id: aircraft.id,
    registration: aircraft.registration,
    typeId: aircraft.typeId,
    status: aircraft.status,
    location: aircraft.location,
    etopsMinutes: aircraft.etopsMinutes,
    maxTakeoffWeightKg: aircraft.maxTakeoffWeightKg,
    ilsCategory: aircraft.ilsCategory,
    fuelOnBoardKg: aircraft.fuelOnBoardKg,
    activeFlightInstanceId: aircraft.activeFlightInstanceId,
    hoursSinceMaintenance: aircraft.hoursSinceMaintenance,
    cyclesSinceCheck: aircraft.cyclesSinceCheck,
    availableFromDay: aircraft.availableFromDay
  };
}

export function airportConfigUpdateFromSet(world: WorldData, airportConfigs: AirportConfigSet, airportCode: string): AirportConfigUpdate {
  const code = airportCode || world.airports[0]?.code || "";
  const ops = airportConfigs.ops.find((config) => config.airportCode === code);
  const fuel = airportConfigs.fuel.find((config) => config.airportCode === code);
  const payload = airportConfigs.payload.find((config) => config.airportCode === code);
  if (!ops || !fuel || !payload) {
    throw new Error(`机场配置缺失：${code}。请刷新机场配置或检查初始化入库。`);
  }
  return {
    airportCode: code,
    ops,
    fuel,
    payload
  };
}

export function formatAuditPayload(payload: unknown): string {
  if (payload === null || payload === undefined) {
    return "--";
  }
  try {
    return JSON.stringify(payload).slice(0, 180);
  } catch {
    return String(payload).slice(0, 180);
  }
}
